import { Link } from 'react-router-dom'
import type { HashedUser, TaskDetail } from 'types/entity'
import { formatDateTime } from 'utils/dotNetDate'
import { Comments } from './Comments'

const AVATAR_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><rect width="48" height="48" fill="#e5f6ec"/><circle cx="24" cy="18" r="8" fill="#bfd4c8"/><path d="M6 47c2-10 9-15 18-15s16 5 18 15z" fill="#bfd4c8"/></svg>`,
  )

/**
 * Port of `CreateTaskView` + `GetTaskData` (and the byte-identical
 * `CreateAppointmentView` + `GetAppointmentData`, which differ only in heading).
 */
export function TaskDetailView({
  task,
  taskId,
  hashedUsers,
  heading,
}: {
  task: TaskDetail
  taskId: string
  hashedUsers: HashedUser[]
  heading: string
}) {
  const createdByUser = hashedUsers.find((user) => String(user.Code) === String(task.CreatedBy))
  const collaborators = (task.TaskCollaborationUsers ?? []).map((c) => c.PersonName).join(', ')
  const relatedLead = task.LeadView

  return (
    <div className="entity-main">
      <h5>{heading}</h5>

      <div className="details-header">
        <img
          className="related-avatar"
          src={relatedLead?.ProfileImgFileName || AVATAR_FALLBACK}
          alt={relatedLead?.PersonName ?? 'Related'}
          onError={(e) => {
            e.currentTarget.src = AVATAR_FALLBACK
          }}
        />
        <div>
          {task.StartDate && relatedLead?.PersonName && (
            <h4 className="lead-title">
              <span className="k">Related To:</span> {relatedLead.PersonName}
            </h4>
          )}
          {relatedLead?.LeadId != null && relatedLead.LeadId !== '' && (
            <div className="font13">
              <span className="k">Lead No.:</span>{' '}
              <Link to={`/view/lead/${relatedLead.LeadId}`}>{relatedLead.LeadId}</Link>
            </div>
          )}
        </div>
      </div>

      <div className="lead-content">
        <h3>{task.Title}</h3>
        {task.Description && <p>{task.Description}</p>}

        <div className="detail-grid">
          <Detail label="Due Date" value={formatDateTime(task.EndDate)} />
          <Detail label="Where" value={task.Address} />
          <Detail label="Complete Address" value={task.CompleteAddress} />
          <Detail label="Completed Date" value={task.CompletedOn} />
          <Detail label="Outcomes" value={task.OutcomeName} />
          <Detail label="Owner" value={task.OwnerName} />
          <Detail label="Collaborators" value={collaborators} />
          <Detail label="Created On" value={formatDateTime(task.CreatedOn)} />
          <Detail label="Created By" value={createdByUser?.Text ?? ''} />
        </div>

        {task.Remarks && (
          <p className="mt-1">
            <span className="k">Remarks:</span> {task.Remarks}
          </p>
        )}
      </div>

      <hr />

      {/* The legacy code only rendered the composer when hasComment was set. */}
      <Comments taskId={taskId} variant="task" canAdd={task.hasComment} />
    </div>
  )
}

/** Rendered only when populated — the legacy code emitted an empty node otherwise. */
function Detail({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div className="detail-item">
      <span className="k">{label}:</span>
      <span>{value}</span>
    </div>
  )
}
